/**
 * ThemesV2Page - Container page for V2 themes list
 *
 * Manages V2 theme CRUD operations and navigation to V2 theme editor.
 * Uses separate Firestore collection (themesV2/) and V2-specific hooks.
 */

import React, { useState, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { SDialog, StyledTextField } from '@modules/ui-components';
import { GenerateThemeFromImageDialog } from '../components/GenerateThemeFromImageDialog';
import { ThemesV2ListView } from '../components/ThemesV2ListView';
import { useThemesV2 } from '../hooks/useThemesV2';
import { useThemeV2Actions } from '../hooks/useThemeV2Actions';
import { createDefaultFlowDocument } from '../factories';
import { defaultThemeV2Mapper } from '../mappers/DefaultThemeV2Mapper';
import {
  DEFAULT_THEME_V2_COLORS,
  DEFAULT_THEME_V2_FONTS,
  DEFAULT_THEME_V2_OPTIONS_LIST_SETTINGS,
  DEFAULT_THEME_V2_LAYOUT_SETTINGS,
  DEFAULT_BACKDROP,
  DEFAULT_HEADER_BAR,
  DEFAULT_ACTION_BAR,
} from '../types/themesV2.types';
import type { CreateThemeV2Input, ThemeV2 } from '../types/themesV2.types';
import { parseThemeImportFileAsV2 } from '@modules/onboardings/services/themeSettingsIOV2';
import { importedThemeSettingsToSystemTheme } from '@modules/themes/utils/themeImport';

const THEME_V2_GLOBAL_ANALYSIS_PROJECT_ID = 'theme-v2-assets';

/**
 * ThemesV2Page - Main page for V2 theme management
 */
export const ThemesV2Page: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { themes, loading, error, hasMore, loadMore, refresh } = useThemesV2();
  const { createTheme, cloneTheme, deleteTheme, loading: actionLoading } = useThemeV2Actions();

  // Create dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newThemeName, setNewThemeName] = useState('');
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);

  // Import state
  const importFileInputRef = useRef<HTMLInputElement>(null);
  const [importNotification, setImportNotification] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });

  // Navigate to V2 theme editor
  const handleEdit = useCallback(
    (themeId: string) => {
      navigate(`/themes-v2/${themeId}${location.search}`);
    },
    [location.search, navigate]
  );

  // Clone theme
  const handleClone = useCallback(
    async (themeId: string) => {
      try {
        await cloneTheme(themeId);
        refresh();
      } catch {
        // Error is handled in hook
      }
    },
    [cloneTheme, refresh]
  );

  // Delete theme
  const handleDelete = useCallback(
    async (themeId: string) => {
      try {
        await deleteTheme(themeId);
        refresh();
      } catch {
        // Error is handled in hook
      }
    },
    [deleteTheme, refresh]
  );

  // Open create dialog
  const handleCreateClick = useCallback(() => {
    setNewThemeName('');
    setCreateDialogOpen(true);
  }, []);

  // Close create dialog
  const handleCreateDialogClose = useCallback(() => {
    setCreateDialogOpen(false);
    setNewThemeName('');
  }, []);

  const handleOpenGenerateDialog = useCallback(() => {
    setGenerateDialogOpen(true);
  }, []);

  const handleCloseGenerateDialog = useCallback(() => {
    setGenerateDialogOpen(false);
  }, []);

  // Create new V2 theme with default template screens
  const handleCreateSubmit = useCallback(async () => {
    if (!newThemeName.trim()) return;

    try {
      // Create default theme settings
      const defaultTheme = {
        name: newThemeName.trim(),
        colors: DEFAULT_THEME_V2_COLORS,
        fonts: DEFAULT_THEME_V2_FONTS,
        optionsListSettings: DEFAULT_THEME_V2_OPTIONS_LIST_SETTINGS,
        layoutSettings: DEFAULT_THEME_V2_LAYOUT_SETTINGS,
        background: DEFAULT_BACKDROP,
        headerBar: DEFAULT_HEADER_BAR,
        actionBar: DEFAULT_ACTION_BAR,
        flowDocument: createDefaultFlowDocument(),
      };

      // Apply theme mapper to populate all screens with default colors/fonts
      const flowDocument = defaultThemeV2Mapper.applyToFlowDocument(
        defaultTheme as ThemeV2,
        defaultTheme.flowDocument
      );

      await createTheme({
        ...defaultTheme,
        flowDocument,
      });
      setCreateDialogOpen(false);
      setNewThemeName('');
      refresh();
    } catch {
      // Error is handled in hook
    }
  }, [newThemeName, createTheme, refresh]);

  const handleGeneratedThemeCreate = useCallback(
    async (themeInput: CreateThemeV2Input) => {
      const cleanTheme = JSON.parse(JSON.stringify(themeInput)) as CreateThemeV2Input;
      await createTheme(cleanTheme);
      setImportNotification({
        open: true,
        message: `Theme "${themeInput.name || 'Generated Image Theme'}" created successfully`,
        severity: 'success',
      });
      refresh();
    },
    [createTheme, refresh]
  );

  // Import V1/V2 theme JSON as new V2 theme
  const handleImportClick = useCallback(() => {
    importFileInputRef.current?.click();
  }, []);

  const handleImportFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;
      event.target.value = '';

      try {
        const content = await file.text();
        const imported = parseThemeImportFileAsV2(content);

        // Build theme name from filename (strip extension and timestamp)
        const baseName = file.name.replace(/\.json$/i, '').replace(/_\d{4}-\d{2}-\d{2}T.*$/, '');
        const themeName = `Imported: ${baseName}`;

        // The full theme, with the exported templates as authored (onboarding theme switch plan, AC-031).
        await createTheme(importedThemeSettingsToSystemTheme(imported, themeName));

        setImportNotification({ open: true, message: `Theme "${themeName}" imported successfully`, severity: 'success' });
        refresh();
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to import theme';
        setImportNotification({ open: true, message, severity: 'error' });
      }
    },
    [createTheme, refresh]
  );

  // Retry on error
  const handleRetry = useCallback(() => {
    refresh();
  }, [refresh]);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          Themes V2
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<AutoAwesomeIcon />}
            onClick={handleOpenGenerateDialog}
            disabled={actionLoading}
            data-testid="v2-theme-generate-from-image-btn"
          >
            Generate From Image
          </Button>
          <Button
            variant="outlined"
            startIcon={<UploadFileIcon />}
            onClick={handleImportClick}
            disabled={actionLoading}
            data-testid="v2-theme-import-btn"
          >
            Import Theme JSON
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateClick}
            disabled={actionLoading}
            data-testid="v2-theme-create-btn"
          >
            Create V2 Theme
          </Button>
        </Box>
      </Box>

      {/* Error state */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <Button color="inherit" size="small" onClick={handleRetry}>
              Retry
            </Button>
          }
        >
          {error.message}
        </Alert>
      )}

      {/* Loading state */}
      {loading && themes.length === 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 200,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {/* V2 Themes list */}
      {!error && (
        <ThemesV2ListView
          themes={themes}
          loading={loading}
          hasMore={hasMore}
          onLoadMore={loadMore}
          onEdit={handleEdit}
          onClone={handleClone}
          onDelete={handleDelete}
        />
      )}

      {/* Create V2 theme dialog */}
      <SDialog
        open={createDialogOpen}
        onClose={handleCreateDialogClose}
        title="Create V2 Theme"
        maxWidth="sm"
        fullWidth
        primaryAction={{
          label: 'Create',
          onClick: handleCreateSubmit,
          disabled: !newThemeName.trim() || actionLoading,
        }}
      >
        <Box sx={{ pt: 1 }}>
          <StyledTextField
            label="Theme Name"
            value={newThemeName}
            onChange={setNewThemeName}
            fullWidth
            autoFocus
            aria-label="Theme Name"
            data-testid="v2-theme-name-input"
          />
        </Box>
      </SDialog>

      <GenerateThemeFromImageDialog
        open={generateDialogOpen}
        projectId={THEME_V2_GLOBAL_ANALYSIS_PROJECT_ID}
        onClose={handleCloseGenerateDialog}
        onCreateTheme={handleGeneratedThemeCreate}
      />

      {/* Hidden file input for theme import */}
      <input
        ref={importFileInputRef}
        type="file"
        hidden
        accept=".json"
        onChange={handleImportFileChange}
      />

      {/* Import notification */}
      <Snackbar
        open={importNotification.open}
        autoHideDuration={4000}
        onClose={() => setImportNotification(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setImportNotification(prev => ({ ...prev, open: false }))}
          severity={importNotification.severity}
          variant="filled"
        >
          {importNotification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ThemesV2Page;
